import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminCourseSaleCreateAction,
  adminCourseSaleGetAction,
  adminCourseSaleUpdateAction,
  adminCoursesListAction
} from "../Action/action";

export default function CourseSaleForm({ mode }) {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  const [name, setName] = useState("");
  const [courseId, setCourseId] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [discount, setDiscount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("active");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCoursesLoading(true);
      try {
        const res = await adminCoursesListAction({ page: 1, per_page: 200 });
        if (cancelled) return;
        setCourses(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseSaleGetAction(saleId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load sale.");
          return;
        }
        const d = res.data || {};
        setName(d.name || "");
        setCourseId(d.course_id != null ? String(d.course_id) : "");
        setDiscountType(d.discount_type === "amount" ? "amount" : "percent");
        setDiscount(d.discount != null ? String(d.discount) : "");
        setStartDate(d.start_date ? String(d.start_date).slice(0, 10) : "");
        setEndDate(d.end_date ? String(d.end_date).slice(0, 10) : "");
        setStatus(String(d.status || "active").toLowerCase() === "inactive" ? "inactive" : "active");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load sale.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, saleId]);

  const validate = () => {
    const errs = {};
    if (!String(name || "").trim()) errs.name = "Name is required.";
    if (!courseId || !Number.isInteger(Number(courseId)) || Number(courseId) < 1) {
      errs.courseId = "Course is required.";
    }
    if (!discount || Number(discount) < 0 || Number.isNaN(Number(discount))) {
      errs.discount = "Discount is required.";
    }
    if (discountType === "percent" && Number(discount) > 100) {
      errs.discount = "Percent cannot be more than 100.";
    }
    if (startDate && endDate && endDate < startDate) {
      errs.endDate = "End date cannot be before start date.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async () => {
    if (saving) return;
    setError("");
    if (!validate()) return;

    const payload = {
      name: String(name || "").trim(),
      course_id: Number(courseId),
      discount_type: discountType,
      discount: Number(discount),
      start_date: startDate || null,
      end_date: endDate || null,
      status
    };

    setSaving(true);
    try {
      if (isEdit) {
        const res = await adminCourseSaleUpdateAction(saleId, payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Update failed");
        toast.success("Sale updated", { toastId: "course-sale-save" });
      } else {
        const res = await adminCourseSaleCreateAction(payload);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Create failed");
        toast.success("Sale created", { toastId: "course-sale-save" });
      }
      navigate("/course-sales");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
      toast.error(e?.response?.data?.message || e?.message || "Save failed", {
        toastId: "course-sale-save"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="toolbar">
        <div className="breadcrumb">
          <Link to="/course-sales">Course Sales</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"} Sale</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/course-sales" className="btn btn-primary">
            ← Back
          </Link>
        </div>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Save failed.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="settings-section">
          <h3>Sale Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label>
                Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setFieldErrors((p) => ({ ...p, name: "" }));
                }}
                placeholder="e.g. Launch week 50%"
                style={fieldErrors.name ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.name ? <div className="text-danger field-error">{fieldErrors.name}</div> : null}
            </div>
            <div className="form-group">
              <label>
                Course <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={courseId}
                onChange={(e) => {
                  setCourseId(e.target.value);
                  setFieldErrors((p) => ({ ...p, courseId: "" }));
                }}
                disabled={coursesLoading}
                style={fieldErrors.courseId ? { borderColor: "var(--danger)" } : undefined}
              >
                <option value="">{coursesLoading ? "Loading courses…" : "Select course"}</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title || `Course #${c.id}`}
                  </option>
                ))}
              </select>
              {fieldErrors.courseId ? (
                <div className="text-danger field-error">{fieldErrors.courseId}</div>
              ) : null}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Discount type <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
              >
                <option value="percent">Percent (%)</option>
                <option value="amount">Amount</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                Discount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={discount}
                onChange={(e) => {
                  setDiscount(e.target.value);
                  setFieldErrors((p) => ({ ...p, discount: "" }));
                }}
                placeholder={discountType === "percent" ? "e.g. 50" : "e.g. 100.00"}
                style={fieldErrors.discount ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.discount ? (
                <div className="text-danger field-error">{fieldErrors.discount}</div>
              ) : null}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setFieldErrors((p) => ({ ...p, endDate: "" }));
                }}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setFieldErrors((p) => ({ ...p, endDate: "" }));
                }}
                style={fieldErrors.endDate ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.endDate ? (
                <div className="text-danger field-error">{fieldErrors.endDate}</div>
              ) : null}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <p className="small-text mt-2">
                Active sales apply automatically in the date window (checkout wiring can use this master data).
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loading}>
          {saving ? "Saving…" : isEdit ? "Update Sale" : "Save Sale"}
        </button>
        <Link to="/course-sales" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
